import { useEffect, useState } from "react";
import fetchWithCredentials from "apiClient";
import { useAuth } from "context/AuthContext";

interface MessagesReportData {
  condoId: number | null;
  condoName: string;
  channels: Record<string, number>;
  periodIndex?: number; // 0, 1, 2 for Semana 1, Semana 2, Esta semana
}

interface MessagesReportResponse {
  data: MessagesReportData[];
}

interface CasesReportData {
  condoId: number;
  condoName: string;
  types: Record<string, number>;
  categories: Record<string, number>;
  periodIndex?: number; // 0, 1, 2 for Semana 1, Semana 2, Esta semana
}

interface CasesReportResponse {
  data: CasesReportData[];
}

type ReportType = "messages" | "cases_by_priority" | "cases_by_category";
type PeriodType = "7_days" | "30_days";

export function useReportData(
  reportType: ReportType,
  period: PeriodType,
  selectedCondoIds: number[]
) {
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";

  const [data, setData] = useState<MessagesReportData[]>([]);
  const [casesData, setCasesData] = useState<CasesReportData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchReport() {
      setLoading(true);
      setError(null);

      try {
        if (reportType === "messages") {
          await fetchMessagesData(period, selectedCondoIds, setData);
          setCasesData([]);
        } else if (reportType === "cases_by_priority") {
          await fetchCasesData(period, selectedCondoIds, setCasesData, "type");
          setData([]);
        } else if (reportType === "cases_by_category") {
          await fetchCasesData(
            period,
            selectedCondoIds,
            setCasesData,
            "category"
          );
          setData([]);
        }
      } catch (err) {
        console.error("Error loading report:", err);
        setError(
          locale === "es"
            ? "Error al cargar los datos del reporte"
            : "Error loading report data"
        );
      } finally {
        setLoading(false);
      }
    }

    fetchReport();
  }, [reportType, period, selectedCondoIds, locale]);

  return { data, casesData, loading, error };
}

async function fetchMessagesData(
  period: PeriodType,
  selectedCondoIds: number[],
  setData: (data: MessagesReportData[]) => void
) {
  // Build URL with condo parameters
  let url = `/manager-api/reports/messages/?period=${period}`;
  if (selectedCondoIds.length > 0) {
    const condoParams = selectedCondoIds.map((id) => `condo=${id}`).join("&");
    url += `&${condoParams}`;
  }

  try {
    const response: MessagesReportResponse = await fetchWithCredentials(url);
    // Backend returns individual periods with periodIndex, use as-is
    setData(response.data);
  } catch (err) {
    console.error("Error loading messages report:", err);
    throw err;
  }
}

async function fetchCasesData(
  period: PeriodType,
  selectedCondoIds: number[],
  setCasesData: (data: CasesReportData[]) => void,
  groupBy: "type" | "category" = "type"
) {
  // Build URL with condo parameters and group_by
  let url = `/manager-api/cases/report/?period=${period}&group_by=${groupBy}`;
  if (selectedCondoIds.length > 0) {
    const condoParams = selectedCondoIds.map((id) => `condo=${id}`).join("&");
    url += `&${condoParams}`;
  }

  try {
    const response: CasesReportResponse = await fetchWithCredentials(url);
    setCasesData(response.data);
  } catch (err) {
    console.error("Error loading cases report:", err);
    throw err;
  }
}
